import React, { useState, useRef, useEffect } from 'react';
import Select from 'react-select';
import Tooltip from '@material-ui/core/Tooltip';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { PROFILES } from '../../../utils/constants';
import Button from '@mui/material/Button';
import { alpha, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { toast } from 'react-toastify';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ClearIcon from '@mui/icons-material/Clear';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import PhoneIcon from '@mui/icons-material/Phone';
import DownloadIcon from '@mui/icons-material/Download';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import AppBar from '@mui/material/AppBar';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import CircularProgress from '@mui/material/CircularProgress';
import { format } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { DateRangePicker } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import Container from '../../GeneralComponents/Container';
import { Topbar, Sidebar, Table } from '../components';
import { deleteEnquiry, getEnquiries, getOrganizations } from '../../../redux/actions/apiActions';
import setGlobalLoader from '../../../redux/actions/loaderActions';
import exportToExcel from '../../../utils/exportToExcel';
import { ENQUIRY_SERVICES, ENQUIRY_STATUSES } from './AddEnquiry';

// #SUGGESTION: Add a "Send Reminder" action button per row that triggers a Twilio SMS
// to the enquiry's contactNumber when the follow-up date is today or overdue.

const getServiceLabel = (value) => ENQUIRY_SERVICES.find((s) => s.value === value)?.label || value;
const getStatusLabel = (value) => ENQUIRY_STATUSES.find((s) => s.value === value)?.label || value;

const STATUS_CHIP_COLORS = {
    new: '#1976d2',
    contacted: '#ed6c02',
    in_progress: '#9c27b0',
    converted: '#2e7d32',
    lost: '#d32f2f',
};

const TableComponent = React.forwardRef(({ columns, data, title, isRemoteData }, ref) => {
    const theme = useTheme();
    return (
        <Box p={4}>
            <Box
                width={1}
                height={1}
                borderRadius={2}
                border={`2px solid ${theme.palette.divider}`}
                sx={{ borderStyle: 'dashed' }}
            >
                <Table
                    ref={ref}
                    columns={columns}
                    data={data}
                    export={false}
                    selection={false}
                    toolbar
                    title={title}
                    isRemoteData={isRemoteData}
                />
            </Box>
        </Box>
    );
});

const Inquiries = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const profile = useSelector((state) => state.authReducer.profile);
    const tableRef = useRef(null);
    const theme = useTheme();
    const isMd = useMediaQuery(theme.breakpoints.up('md'), { defaultMatches: true });

    const [openSidebar, setOpenSidebar] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState(null);
    const [organizations, setOrganizations] = useState([]);
    const [selectedOrganization, setSelectedOrganization] = useState(null);
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [todayFollowUps, setTodayFollowUps] = useState([]);
    const [followUpsLoading, setFollowUpsLoading] = useState(false);

    const today = new Date();
    const minDateObj = new Date('2025-01-01');

    const getDefaultStartDate = () => (profile === PROFILES.ADMIN ? new Date(today.getFullYear(), today.getMonth(), 1) : null);
    const getDefaultEndDate = () => (profile === PROFILES.ADMIN ? today : null);

    const [dateRange, setDateRange] = useState([
        { startDate: getDefaultStartDate(), endDate: getDefaultEndDate(), key: 'selection' },
    ]);

    const startDate = dateRange[0].startDate;
    const endDate = dateRange[0].endDate;

    const handleSidebarOpen = () => setOpenSidebar(true);
    const handleSidebarClose = () => setOpenSidebar(false);
    const open = isMd ? false : openSidebar;

    useEffect(() => {
        if (profile === PROFILES.ADMIN) {
            async function fetchOrganizations() {
                try {
                    const orgRes = await getOrganizations('?limit=10000');
                    const data = (orgRes?.data?.data?.docs || []).map((d) => ({
                        label: d?.name,
                        value: d?._id,
                    }));
                    setOrganizations(data);
                } catch (error) {
                    console.log('get organizations error', error);
                }
            }
            fetchOrganizations();
        }
    }, [profile]);

    const refreshTodayFollowUps = async () => {
        setFollowUpsLoading(true);
        try {
            const res = await getEnquiries('?followUpToday=true&limit=1000');
            const docs = res?.data?.data?.docs || [];
            setTodayFollowUps(docs.map((item) => ({
                ...item,
                serviceLabel: getServiceLabel(item.service),
                statusLabel: getStatusLabel(item.status),
            })));
        } catch (error) {
            console.log('get today follow-ups error', error);
        } finally {
            setFollowUpsLoading(false);
        }
    };

    useEffect(() => {
        if (profile === PROFILES.ORGANIZATION) {
            refreshTodayFollowUps();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profile]);

    const handleDelete = async (rowData) => {
        if (window.confirm('Are you sure you want to delete this enquiry?')) {
            dispatch(setGlobalLoader(true));
            try {
                await deleteEnquiry(rowData._id);
                dispatch(setGlobalLoader(false));
                toast.success('Enquiry deleted successfully');
                if (tableRef.current) tableRef.current.onQueryChange();
                if (profile === PROFILES.ORGANIZATION) refreshTodayFollowUps();
            } catch (error) {
                dispatch(setGlobalLoader(false));
                console.log('delete enquiry error', error);
                toast.error(error?.response?.data?.message ?? 'Error deleting enquiry');
            }
        } else {
            toast.info('Request Cancelled');
        }
    };

    const columns = [
        { title: 'Sr No.', field: 'index', filtering: false },
        { title: 'Name', field: 'name', filtering: false },
        { title: 'Contact', field: 'contactNumber', filtering: false },
        { title: 'Service', field: 'serviceLabel', filtering: false },
        {
            title: 'Status',
            field: 'statusLabel',
            filtering: false,
            render: (rowData) => (
                <Box
                    component="span"
                    sx={{
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 1,
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        color: '#fff',
                        backgroundColor: STATUS_CHIP_COLORS[rowData.status] || '#757575',
                    }}
                >
                    {rowData.statusLabel}
                </Box>
            ),
        },
        { title: 'Follow Up Date', field: 'followUpDateDisplay', filtering: false },
        { title: 'Created', field: 'createdAtDisplay', filtering: false },
        ...(profile !== PROFILES.ADMIN
            ? [
                  {
                      title: 'Actions',
                      field: '',
                      filtering: false,
                      render: (rowData) => (
                          <div className="tableActions">
                              <Tooltip title="Edit">
                                  <EditIcon
                                      onClick={() =>
                                          navigate(`/enquiry/edit/${rowData._id}`, {
                                              state: { EnquiryData: rowData },
                                          })
                                      }
                                  />
                              </Tooltip>
                              <Tooltip title="Delete">
                                  <DeleteIcon onClick={() => handleDelete(rowData)} />
                              </Tooltip>
                          </div>
                      ),
                  },
              ]
            : []),
    ];

    const fetchInquiries = async (query) => {
        return new Promise(async (resolve) => {
            try {
                dispatch(setGlobalLoader(true));
                const page = query.page + 1;
                const limit = query.pageSize;

                const queryParams = new URLSearchParams({
                    page: page.toString(),
                    limit: limit.toString(),
                });

                if (selectedStatus?.value) {
                    queryParams.append('status', selectedStatus.value);
                }
                if (selectedOrganization?.value) {
                    queryParams.append('organizationId', selectedOrganization.value);
                }
                if (startDate) {
                    queryParams.append('startDate', format(startDate, 'yyyy-MM-dd'));
                }
                if (endDate) {
                    queryParams.append('endDate', format(endDate, 'yyyy-MM-dd'));
                }

                const res = await getEnquiries(`?${queryParams.toString()}`);
                const apiData = res?.data?.data;

                const data = (apiData?.docs || []).map((item, i) => ({
                    ...item,
                    index: (page - 1) * limit + i + 1,
                    serviceLabel: getServiceLabel(item.service),
                    statusLabel: getStatusLabel(item.status),
                    followUpDateDisplay: item.followUpDate
                        ? format(new Date(item.followUpDate), 'MMM dd, yyyy')
                        : '—',
                    createdAtDisplay: item.createdAt
                        ? format(new Date(item.createdAt), 'MMM dd, yyyy')
                        : '—',
                }));

                dispatch(setGlobalLoader(false));
                resolve({ data, page: query.page, totalCount: apiData?.total || 0 });
            } catch (error) {
                dispatch(setGlobalLoader(false));
                console.log('get enquiry error', error);
                toast.error(error?.response?.data?.message ?? 'Error fetching enquiry');
                resolve({ data: [], page: 0, totalCount: 0 });
            }
        });
    };

    const handleDownload = async () => {
        try {
            dispatch(setGlobalLoader(true));
            const queryParams = new URLSearchParams({ page: '1', limit: '10000' });
            if (selectedStatus?.value) queryParams.append('status', selectedStatus.value);
            if (selectedOrganization?.value) queryParams.append('organizationId', selectedOrganization.value);
            if (startDate) queryParams.append('startDate', format(startDate, 'yyyy-MM-dd'));
            if (endDate) queryParams.append('endDate', format(endDate, 'yyyy-MM-dd'));

            const res = await getEnquiries(`?${queryParams.toString()}`);
            const docs = res?.data?.data?.docs || [];
            if (docs.length === 0) { toast.info('No data to download.'); return; }

            const rows = docs.map((item, i) => ({
                'Sr No.': i + 1,
                'Name': item.name || '',
                'Contact': item.contactNumber || '',
                'Service': getServiceLabel(item.service),
                'Status': getStatusLabel(item.status),
                'Follow Up Date': item.followUpDate ? format(new Date(item.followUpDate), 'MMM dd, yyyy') : '',
                'Notes': item.notes || '',
                'Created': item.createdAt ? format(new Date(item.createdAt), 'MMM dd, yyyy') : '',
            }));
            await exportToExcel(
                rows,
                ['Sr No.', 'Name', 'Contact', 'Service', 'Status', 'Follow Up Date', 'Notes', 'Created'],
                'Enquiries'
            );
            toast.success('Downloaded successfully.');
        } catch (err) {
            toast.error('Error downloading enquiries.');
        } finally {
            dispatch(setGlobalLoader(false));
        }
    };

    const handleFilter = () => {
        if (tableRef.current) tableRef.current.onQueryChange();
    };

    const handleReset = () => {
        setSelectedStatus(null);
        setSelectedOrganization(null);
        setDateRange([
            { startDate: getDefaultStartDate(), endDate: getDefaultEndDate(), key: 'selection' },
        ]);
        setShowDatePicker(false);
        setTimeout(() => {
            if (tableRef.current) tableRef.current.onQueryChange();
        }, 100);
    };

    return (
        <Box>
            <AppBar
                position={'fixed'}
                sx={{
                    backgroundColor: theme.palette.background.paper,
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                }}
                elevation={0}
            >
                <Container maxWidth={1} paddingY={{ xs: 1, sm: 1.5 }}>
                    <Topbar onSidebarOpen={handleSidebarOpen} />
                </Container>
            </AppBar>
            <Sidebar onClose={handleSidebarClose} open={open} variant={isMd ? 'permanent' : 'temporary'} />
            <main>
                <Box height={{ xs: 94, sm: 102, md: 107 }} />
                <Box
                    display="flex"
                    flex="1 1 auto"
                    overflow="hidden"
                    paddingLeft={{ md: '256px' }}
                >
                    <Box display="flex" flex="1 1 auto" overflow="hidden">
                        <Box flex="1 1 auto" height="100%" overflow="auto">
                            {/* Today's Follow-ups — organization only */}
                            {profile === PROFILES.ORGANIZATION && (
                                <Box px={{ xs: 3, md: 4 }} pt={3}>
                                    <Paper
                                        variant="outlined"
                                        sx={{
                                            borderColor: '#ed6c02',
                                            borderWidth: 2,
                                            borderRadius: 2,
                                            overflow: 'hidden',
                                        }}
                                    >
                                        {/* Header */}
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            gap={1.5}
                                            px={3}
                                            py={1.5}
                                            sx={{ backgroundColor: '#fff8f0' }}
                                        >
                                            <NotificationsActiveIcon sx={{ color: '#ed6c02' }} />
                                            <Typography variant="subtitle1" fontWeight={700} color="#ed6c02">
                                                Today's Follow-ups
                                            </Typography>
                                            {!followUpsLoading && (
                                                <Chip
                                                    label={todayFollowUps.length}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: todayFollowUps.length > 0 ? '#ed6c02' : '#bdbdbd',
                                                        color: '#fff',
                                                        fontWeight: 700,
                                                        ml: 0.5,
                                                    }}
                                                />
                                            )}
                                            <Box flex={1} />
                                            <Button
                                                size="small"
                                                variant="text"
                                                sx={{ color: '#ed6c02', textTransform: 'none' }}
                                                onClick={refreshTodayFollowUps}
                                                disabled={followUpsLoading}
                                            >
                                                Refresh
                                            </Button>
                                        </Box>

                                        <Divider sx={{ borderColor: '#ed6c02', opacity: 0.3 }} />

                                        {/* Content */}
                                        <Box px={3} py={2}>
                                            {followUpsLoading ? (
                                                <Box display="flex" justifyContent="center" py={2}>
                                                    <CircularProgress size={24} sx={{ color: '#ed6c02' }} />
                                                </Box>
                                            ) : todayFollowUps.length === 0 ? (
                                                <Typography variant="body2" color="text.secondary" py={1}>
                                                    No follow-ups due today. You're all caught up!
                                                </Typography>
                                            ) : (
                                                <Box display="flex" flexDirection="column" gap={1.5}>
                                                    {todayFollowUps.map((item) => (
                                                        <Box
                                                            key={item._id}
                                                            display="flex"
                                                            alignItems="center"
                                                            gap={2}
                                                            flexWrap="wrap"
                                                            sx={{
                                                                py: 1,
                                                                px: 1.5,
                                                                borderRadius: 1,
                                                                backgroundColor: '#fffaf5',
                                                                border: '1px solid #ffe0b2',
                                                            }}
                                                        >
                                                            {/* Name */}
                                                            <Typography variant="body2" fontWeight={600} minWidth={120}>
                                                                {item.name}
                                                            </Typography>

                                                            {/* Contact */}
                                                            <Box
                                                                component="a"
                                                                href={`tel:${item.contactNumber}`}
                                                                display="flex"
                                                                alignItems="center"
                                                                gap={0.5}
                                                                sx={{
                                                                    textDecoration: 'none',
                                                                    color: 'inherit',
                                                                    '&:hover': { color: '#ed6c02' },
                                                                }}
                                                            >
                                                                <PhoneIcon sx={{ fontSize: 14 }} />
                                                                <Typography variant="body2">
                                                                    {item.contactNumber}
                                                                </Typography>
                                                            </Box>

                                                            {/* Service */}
                                                            <Chip
                                                                label={item.serviceLabel}
                                                                size="small"
                                                                variant="outlined"
                                                                sx={{ fontSize: '0.7rem' }}
                                                            />

                                                            {/* Status */}
                                                            <Box
                                                                component="span"
                                                                sx={{
                                                                    px: 1.2,
                                                                    py: 0.3,
                                                                    borderRadius: 1,
                                                                    fontSize: '0.7rem',
                                                                    fontWeight: 600,
                                                                    color: '#fff',
                                                                    backgroundColor: STATUS_CHIP_COLORS[item.status] || '#757575',
                                                                }}
                                                            >
                                                                {item.statusLabel}
                                                            </Box>

                                                            {/* Notes */}
                                                            {item.notes && (
                                                                <Typography
                                                                    variant="body2"
                                                                    color="text.secondary"
                                                                    sx={{
                                                                        flex: 1,
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis',
                                                                        whiteSpace: 'nowrap',
                                                                        maxWidth: 200,
                                                                        fontStyle: 'italic',
                                                                    }}
                                                                >
                                                                    {item.notes}
                                                                </Typography>
                                                            )}

                                                            {/* Edit action */}
                                                            <Tooltip title="Edit">
                                                                <EditIcon
                                                                    sx={{ fontSize: 18, cursor: 'pointer', color: 'text.secondary', ml: 'auto' }}
                                                                    onClick={() =>
                                                                        navigate(`/enquiry/edit/${item._id}`, {
                                                                            state: { EnquiryData: item },
                                                                        })
                                                                    }
                                                                />
                                                            </Tooltip>
                                                        </Box>
                                                    ))}
                                                </Box>
                                            )}
                                        </Box>
                                    </Paper>
                                </Box>
                            )}

                            {/* Filters */}
                            <Box px={{ xs: 3, md: 4 }} pt={3}>
                                <Typography variant="subtitle2" fontWeight={700} mb={2}>
                                    Filters
                                </Typography>
                                <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
                                    {/* Date Range Picker */}
                                    <Box sx={{ position: 'relative' }}>
                                        <TextField
                                            size="small"
                                            value={
                                                startDate && endDate
                                                    ? `${format(startDate, 'MMM dd, yyyy')} - ${format(endDate, 'MMM dd, yyyy')}`
                                                    : ''
                                            }
                                            placeholder="Select date range"
                                            onClick={() => setShowDatePicker(!showDatePicker)}
                                            InputProps={{
                                                readOnly: true,
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <CalendarMonthIcon />
                                                    </InputAdornment>
                                                ),
                                                endAdornment: startDate && endDate && (
                                                    <InputAdornment position="end">
                                                        <ClearIcon
                                                            sx={{ cursor: 'pointer' }}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setDateRange([{ startDate: null, endDate: null, key: 'selection' }]);
                                                                setShowDatePicker(false);
                                                            }}
                                                        />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{ minWidth: 280, cursor: 'pointer' }}
                                        />
                                        {showDatePicker && (
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    top: '100%',
                                                    left: 0,
                                                    zIndex: 1000,
                                                    mt: 1,
                                                    boxShadow: 3,
                                                    borderRadius: 1,
                                                    bgcolor: 'background.paper',
                                                }}
                                            >
                                                <DateRangePicker
                                                    onChange={(item) => setDateRange([item.selection])}
                                                    ranges={[{
                                                        startDate: dateRange[0].startDate || new Date(),
                                                        endDate: dateRange[0].endDate || new Date(),
                                                        key: 'selection',
                                                    }]}
                                                    minDate={minDateObj}
                                                    maxDate={today}
                                                    rangeColors={[theme.palette.primary.main]}
                                                    locale={enUS}
                                                />
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent: 'flex-end',
                                                        p: 1,
                                                        borderTop: `1px solid ${theme.palette.divider}`,
                                                    }}
                                                >
                                                    <Button size="small" onClick={() => setShowDatePicker(false)}>
                                                        Close
                                                    </Button>
                                                </Box>
                                            </Box>
                                        )}
                                    </Box>

                                    {/* Status Filter */}
                                    <Select
                                        styles={{
                                            menu: (provided) => ({ ...provided, zIndex: 9999 }),
                                            control: (provided) => ({ ...provided, minHeight: 40, minWidth: 200 }),
                                        }}
                                        placeholder="Filter by Status"
                                        value={selectedStatus}
                                        options={ENQUIRY_STATUSES}
                                        isClearable
                                        onChange={(e) => setSelectedStatus(e)}
                                    />

                                    {/* Organization Filter - Admin only */}
                                    {profile === PROFILES.ADMIN && (
                                        <Select
                                            styles={{
                                                menu: (provided) => ({ ...provided, zIndex: 9999 }),
                                                control: (provided) => ({ ...provided, minHeight: 40, minWidth: 250 }),
                                            }}
                                            placeholder="Select Organization"
                                            value={selectedOrganization}
                                            options={organizations}
                                            isClearable
                                            onChange={(e) => setSelectedOrganization(e)}
                                        />
                                    )}

                                    <Button variant="outlined" color="primary" size="medium" onClick={handleFilter}>
                                        Filter
                                    </Button>
                                    <Button variant="text" color="error" size="medium" onClick={handleReset}>
                                        Reset
                                    </Button>
                                </Box>
                            </Box>

                            {/* Add + Download buttons */}
                            <Box marginTop={3} mx={{ xs: 3, md: 4 }} display="flex" gap={2} alignItems="center" flexWrap="wrap">
                                {profile === PROFILES.ORGANIZATION && (
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        size="large"
                                        onClick={() => navigate('/enquiry/add')}
                                    >
                                        Add Enquiry
                                    </Button>
                                )}
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    size="large"
                                    startIcon={<DownloadIcon />}
                                    onClick={handleDownload}
                                >
                                    Download
                                </Button>
                            </Box>

                            <TableComponent
                                ref={tableRef}
                                data={fetchInquiries}
                                columns={columns}
                                title="Enquiries"
                                isRemoteData={true}
                            />
                            <Divider />
                        </Box>
                    </Box>
                </Box>
            </main>
        </Box>
    );
};

export default Inquiries;
